/* eslint-disable no-nested-ternary */
import { Suspense, useContext } from 'react';
import { isEmpty } from 'lodash';
import { Done, DoneAll } from '@mui/icons-material';
import {
  useNavigate,
  useParams,
  useLocation,
  createSearchParams,
} from 'react-router';
import qs from 'qs';
import { darken, lighten, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Divider,
  Grid2,
  Tooltip,
  Typography,
  Skeleton,
  Drawer,
  Toolbar,
} from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';
import Alert from 'components/alerts';
import {
  ParametersContext,
  ProjectPermissionContext,
  AssetContext,
} from 'context';
import Filters from 'components/filters';
import handleFilters from 'components/filters/hooks/index';
import { DataGrid } from 'components/tables';
import config from 'config';
import WarningDialog from 'components/dialogs/warning';
import { StyledBox } from '../parameters-page-styles';
import Header from './header';
import handleParameterList from './hooks/handle-parameter-list';
import ParameterListTags from './parameter-list-tags';

function ParameterList() {
  const { parameterId } = useParams();
  const { search } = useLocation();
  const searchString = { ...qs.parse(search, { ignoreQueryPrefix: true }) };
  const navigate = useNavigate();
  const theme = useTheme();
  const { approvePermission, checkPermission, writePermission } = useContext(
    ProjectPermissionContext
  );
  const { parameters, loading, error, setError, after } =
    useContext(ParametersContext);
  const { theAsset } = useContext(AssetContext);

  const {
    handleOnRowsScrollEnd,
    paramFiltersActiveCount,
    setParamFiltersActiveCount,
    updateParameterStatus,
    columns,
    resetToParamListView,
    paramSelected,
    setParamSelected,
    selectedParameters,
    setSelectedParameters,
    getTogglableColumns,
    fetchValueOptions,
    setValueOptions,
    fetchUnitOptions,
    setUnitOptions,
    errors,
    rowsWithErrors,
    saveParameterEdits,
    apiRef,
    hasUnsavedRows,
    isSaving,
    setIsSaving,
    unsavedChangesRef,
    saveWarningOpen,
    setSaveWarningOpen,
    handleProcessRowUpdateError,
    discardChanges,
    saveChanges,
    processRowUpdate,
    setFocusCell,
  } = handleParameterList();

  const {
    sortByOptions,
    filters,
    applyFilters,
    filtersMap,
    handleOnChange,
    handleOnCancel,
    clearFilters,
    isFilterActive,
    handleFilterActive,
  } = handleFilters(setParamFiltersActiveCount, resetToParamListView);

  const CustomFooterStatusComponent = () => {
    return (
      <>
        <Divider />
        <Grid2 aria-label="customFooter" container justifyContent="flex-end">
          <Typography variant="body2" sx={{ padding: theme.spacing(1) }}>
            {after
              ? `Loaded first ${parameters?.length} rows.`
              : `Total Rows: ${parameters?.length}`}
          </Typography>
        </Grid2>
      </>
    );
  };

  const renderToolTip = button => {
    return selectedParameters.length &&
      selectedParameters.every(
        param => param?.selectedEntry?.status === 'checked' && approvePermission
      )
      ? 'Mark Selected as approved'
      : selectedParameters.length &&
          selectedParameters.every(
            param =>
              param?.selectedEntry?.status === 'answered' && checkPermission
          )
        ? 'Mark Selected as checked'
        : !selectedParameters.length || button
          ? 'Select Parameters for Bulk Operations'
          : "You don't have permission to do this";
  };

  return (
    <>
      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Suspense fallback={null}>
          <StyledBox aria-label="parameterList">
            <Suspense fallback={null}>
              <Header
                handleFilterActive={handleFilterActive}
                isFilterActive={isFilterActive}
                filterCount={paramFiltersActiveCount}
                setFilterCount={setParamFiltersActiveCount}
                saveChanges={saveChanges}
                discardChanges={discardChanges}
                hasUnsavedRows={hasUnsavedRows}
                isSaving={isSaving}
                errors={errors}
              />
            </Suspense>
            <Grid2
              sx={{
                marginTop: theme.spacing(2),
                marginLeft: theme.spacing(4),
                marginRight: theme.spacing(4),
              }}
            >
              <Drawer
                anchor="right"
                open={isFilterActive}
                onClose={handleOnCancel}
                slotProps={{
                  paper: {
                    sx: {
                      width: '40vw',
                    },
                  },
                }}
              >
                <Toolbar />
                <Suspense fallback={null}>
                  <Filters
                    sortByOptions={sortByOptions}
                    filters={filters}
                    applyFilters={applyFilters}
                    filtersMap={filtersMap}
                    handleOnChange={handleOnChange}
                    handleOnCancel={handleOnCancel}
                    clearFilters={clearFilters}
                  />
                </Suspense>
              </Drawer>
              {!loading && error && (
                <div style={{ padding: theme.spacing(2) }}>
                  <Alert
                    title={error.msg}
                    text={error.details}
                    onClose={() => setError(null)}
                  />
                </div>
              )}
              {loading && <Skeleton width="93%" height={100} />}
              {!loading && (
                <>
                  {config.featureFlags.isBulkQAEnabled && (
                    <Tooltip title={renderToolTip()}>
                      <span>
                        <Button
                          disabled={
                            isEmpty(selectedParameters) ||
                            !(
                              (selectedParameters.every(
                                param =>
                                  param?.selectedEntry?.status === 'checked'
                              ) &&
                                approvePermission) ||
                              (selectedParameters.every(
                                param =>
                                  param?.selectedEntry?.status === 'answered'
                              ) &&
                                checkPermission)
                            )
                          }
                          aria-label="bulk-qa-button"
                          onClick={updateParameterStatus}
                          startIcon={
                            selectedParameters.every(
                              param =>
                                param?.selectedEntry?.status === 'checked'
                            ) ? (
                              <DoneAll />
                            ) : (
                              <Done />
                            )
                          }
                        >
                          {renderToolTip(true)}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <DataGrid
                    footer={CustomFooterStatusComponent}
                    editable={true}
                    showCellVerticalBorder
                    data-cy="parametersGrid"
                    aria-label="parameter-type-table"
                    isRowSelectable={params => !params.row.deletedAt}
                    getRowId={row => row.id}
                    showToolbar
                    processRowUpdate={processRowUpdate}
                    apiRef={apiRef}
                    onRowsScrollEnd={handleOnRowsScrollEnd}
                    loading={loading || isSaving}
                    getTogglableColumns={getTogglableColumns}
                    columnBuffer={5}
                    rows={parameters}
                    columns={columns}
                    toggleToolbar="parameters"
                    editMode="row"
                    rowBufferPx={100}
                    hideFooterPagination={false}
                    // DetailPanelContentComponent={getEditEntryPanelContent}
                    density="standard"
                    sortingOrder={['asc', 'desc']}
                    checkboxSelection={
                      !!config.featureFlags.isParameterQAEnabled
                    }
                    // eslint-disable-next-line no-confusing-arrow
                    getRowClassName={params =>
                      !isEmpty(errors[params.id])
                        ? 'error-cell'
                        : params.row.rowClass
                          ? params.row.rowClass
                          : ''
                    }
                    sx={{
                      [`& .${gridClasses.row}.row--edited`]: {
                        backgroundColor: theme.palette.action.selected,
                      },
                      [`& .${gridClasses.row}.error-cell`]: {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? darken(theme.palette.error.dark, 0.5)
                            : lighten(theme.palette.error.light, 0.5),
                      },
                    }}
                    height={parameters.length ? '55vh' : 300}
                    rowHeight={50}
                    noRowsMessage="No parameters: Use the add parameter button to create new parameters in this asset."
                    disableColumnFilter
                    rowSelectionModel={paramSelected}
                    disableRowSelectionOnClick
                    onRowEditStart={params => {
                      setValueOptions([]);
                      setUnitOptions([]);
                      fetchValueOptions(params.row);
                      fetchUnitOptions(params.row);
                      setFocusCell({ id: params.id, field: params.field });
                    }}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    onRowSelectionModelChange={newRowSelectionModel => {
                      setParamSelected(newRowSelectionModel);
                      setSelectedParameters(
                        parameters.filter(({ id }) =>
                          newRowSelectionModel.includes(id)
                        )
                      );
                    }}
                    onCellDoubleClick={() => {
                      if (!writePermission) {
                        setError({
                          details:
                            'You do not have permission to edit this parameter.',
                          msg: 'Permissions Error',
                        });
                      }
                    }}
                    onCellClick={params => {
                      if (
                        !params.row.deletedAt &&
                        params.field === 'parameterTypeName'
                      ) {
                        if (params.row.id === parameterId) {
                          resetToParamListView();
                        } else {
                          navigate({
                            pathname: `${params.row.id}`,
                            search: `?${createSearchParams({
                              ...searchString,
                            })}`,
                          });
                        }
                      }
                    }}
                    onCellKeyDown={(params, event) => {
                      const editableFields = ['value', 'source']; // Define the editable fields
                      if (params.row.parameterType?.unitTypeId) {
                        editableFields.splice(1, 0, 'unit');
                      }
                      const currentFieldIndex = editableFields.indexOf(
                        params.field
                      );
                      if (
                        event.key === 'ArrowRight' &&
                        currentFieldIndex < editableFields.length - 1
                      ) {
                        const nextField = editableFields[currentFieldIndex + 1];
                        if (nextField) {
                          setFocusCell({ id: params.id, field: nextField });
                        }
                      } else if (
                        event.key === 'ArrowLeft' &&
                        currentFieldIndex > 0
                      ) {
                        const previousField =
                          editableFields[currentFieldIndex - 1];
                        if (previousField) {
                          setFocusCell({
                            id: params.id,
                            field: previousField,
                          });
                        }
                      }
                    }}
                    isCellEditable={params => {
                      if (!writePermission) {
                        return false;
                      }
                      if (theAsset?.deletedAt) {
                        return false;
                      }
                      if (params.field === 'unit') {
                        // Make editable only if parameterType.unitTypeId exists
                        return !!params.row.parameterType?.unitTypeId;
                      }
                      return true; // Default to editable for other fields
                    }}
                  />
                </>
              )}
            </Grid2>
            <Divider sx={{ margin: theme.spacing(2) }} />
            {!theAsset?.deletedAt && <ParameterListTags />}
          </StyledBox>
        </Suspense>
      </Box>
      <WarningDialog
        open={
          saveWarningOpen &&
          Object.values(errors).some(error => !isEmpty(error))
        }
        primaryButtonText="Close"
        handleAction={() => {
          setSaveWarningOpen(false);
          setIsSaving(false);
        }}
        handleClose={() => {
          setSaveWarningOpen(false);
          setIsSaving(false);
        }}
        title={'Parameter Edit Error'}
        body={`Parameter(s) "${rowsWithErrors}" have errors and cannot be saved. Please fix the errors before saving.`}
      />
      <WarningDialog
        open={
          saveWarningOpen &&
          !Object.values(errors).some(error => !isEmpty(error))
        }
        primaryButtonText="Save"
        secondaryButton
        secondaryButtonText="Cancel"
        handleAction={saveParameterEdits}
        handleClose={() => {
          setSaveWarningOpen(false);
          setIsSaving(false);
        }}
        title={'Save Parameter Changes'}
        body={`Save changes to ${Object.keys(unsavedChangesRef.current.unsavedRows).length} parameter(s)?`}
      />
    </>
  );
}

export default ParameterList;
